import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { Card, Input, Typography } from "@material-tailwind/react";
import { handleErrorMessage } from "../../utility";
import api from "../../services/apiService";
import ApiRoutes from "../../services/apiRoutes";
import { useMutation } from "react-query";
import * as yup from "yup";
import ErrorParagraph from "../../components/common/ErrorParagraph";
import LoadingButton from "../../components/common/buttons/LoadingButton";
import logo from "../../assets/images/logo.svg";
import { routerPaths } from "../../services/path";
import { useState } from "react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { useForgotPasswordStore } from "../../store/onboarding/Onboarding";
import { toast } from "react-toastify";

const ResetPasswordValidation = yup.object().shape({
  otp: yup.string().required("OTP is required"),
  password: yup
    .string()
    .required("password is required")
    .matches(/(?=.*[a-z])/, "Password must contain a lowercase letter")
    .matches(/(?=.*[A-Z])/, "Password must contain an uppercase letter")
    .matches(/(?=.*[0-9])/, "Password must contain a number")
    .matches(/(?=.[!@#$%^&])/, "Password must contain a special character")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm password is required"),
});

function ResetPassword() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ResetPasswordValidation),
    mode: "onChange",
  });

  const { email } = useForgotPasswordStore();
  const navigate = useNavigate();
  const [passwordShown, setPasswordShown] = useState(false);
  const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
  const resetPasswordMutation = useMutation(
    (data: { otp: string; password: string; confirmPassword: string }) =>
      api.post(ApiRoutes.resetPassword, {
        email: email,
        otp: data.otp,
        newPassword: data.password,
      }),
    {
      onSuccess: () => {
        navigate(routerPaths.signIn);
        toast.success("Password reset successful. Please sign in.");
      },
      onError: (error) => {
        handleErrorMessage(
          error,
          "Unable to send password reset email. Please try again later."
        );
      },
    }
  );

  const onSubmit = (data: {
    otp: string;
    password: string;
    confirmPassword: string;
  }) => {
    console.log(data);
    if (email === undefined) {
      navigate(routerPaths.forgotPassword);
      return;
    }
    resetPasswordMutation.mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="lg:flex w-screen md:h-screen bg-gray-50">
        <div className="invisible lg:visible bg-map-background bg-no-repeat w-3/6 relative flex flex-col justify-center items-center">
          <img
            className="  absolute md:top-10 md:left-20"
            src={logo}
            alt="GIGM Logo"
          />
          <div className="text-4xl">
            <h1>It’s easier to get</h1>
            <h2 className="inline">started with{` `}</h2>
            <h2 className="inline font-extrabold text-primary">GIGM Boat</h2>
          </div>
        </div>
        <img className="ml-20 md:hidden" src={logo} alt="GIGM Logo" />
        <div className="flex items-center justify-center ">
          <div className="md:w-[60%] md:m-24 m-10">
            <h2 className=" text-2xl text-center">Forgot Password</h2>
            <h2 className=" text-sm text-center mt-5 text-gray-600">
              Hi there, kindly provide your otp and new password
            </h2>
            <Card placeholder={undefined} color="transparent" shadow={false}>
              <div className="mt-8 mb-2 w-80 max-w-screen-lg sm:w-96 my-auto ">
                <div className="mb-1 flex flex-col gap-6">
                  <Typography
                    placeholder={undefined}
                    variant="paragraph"
                    color="blue-gray"
                    className="-mb-3"
                  >
                    Otp
                  </Typography>
                  <Input
                    size="lg"
                    placeholder="Otp"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900 bg-white"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    crossOrigin={undefined}
                    {...register("otp")}
                  />
                  {errors.otp && (
                    <ErrorParagraph message={errors.otp.message!} />
                  )}
                  <Typography
                    placeholder={undefined}
                    variant="paragraph"
                    color="blue-gray"
                    className="-mb-3"
                  >
                    Password
                  </Typography>
                  <Input
                    type={passwordShown ? "text" : "password"}
                    size="lg"
                    placeholder="********"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900 bg-white"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    icon={
                      passwordShown ? (
                        <MdVisibility onClick={() => setPasswordShown(false)} />
                      ) : (
                        <MdVisibilityOff
                          onClick={() => setPasswordShown(true)}
                        />
                      )
                    }
                    crossOrigin={undefined}
                    {...register("password")}
                  />
                  {errors.password && (
                    <ErrorParagraph message={errors.password.message!} />
                  )}
                  <Typography
                    placeholder={undefined}
                    variant="paragraph"
                    color="blue-gray"
                    className="-mb-3"
                  >
                    Confirm Password
                  </Typography>
                  <Input
                    type={confirmPasswordShown ? "text" : "password"}
                    size="lg"
                    placeholder="********"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900 bg-white"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    icon={
                      confirmPasswordShown ? (
                        <MdVisibility
                          onClick={() => setConfirmPasswordShown(false)}
                        />
                      ) : (
                        <MdVisibilityOff
                          onClick={() => setConfirmPasswordShown(true)}
                        />
                      )
                    }
                    crossOrigin={undefined}
                    {...register("confirmPassword")}
                  />
                  {errors.confirmPassword && (
                    <ErrorParagraph message={errors.confirmPassword.message!} />
                  )}
                  <LoadingButton isLoading={resetPasswordMutation.isLoading}>
                    Send Reset Link
                  </LoadingButton>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </form>
  );
}

export default ResetPassword;
